import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ConversationDto, GameSessionDto } from '@initiative/shared';
import { Avatar } from '../../components/Avatar.js';
import { EmptyState, Spinner } from '../../components/Feedback.js';
import { Sheet } from '../../components/Sheet.js';
import { api } from '../../lib/api.js';
import { useChat } from '../../state/chat.js';
import { useMyId } from '../../state/session.js';
import { toast } from '../../state/ui.js';
import { conversationLabel, errorMessage, pressenAufstellung, useGameCatalog } from './helpers.js';

interface GameSetupProps {
  open: boolean;
  onClose: () => void;
  /** Preselected chat; with `lockConversation` it cannot be changed. */
  conversationId?: string | null;
  lockConversation?: boolean;
  /** Preselected game. */
  gameKey?: string | null;
  onCreated?: (session: GameSessionDto) => void;
}

function memberLabel(conversation: ConversationDto, userId: string): string {
  const member = conversation.members.find((item) => item.userId === userId);
  if (!member) return 'Mitglied';
  const nickname = member.nickname?.trim();
  return nickname && nickname.length > 0 ? nickname : member.user.displayName;
}

/**
 * Start a match: pick the game, the chat and – in groups – who plays.
 *
 * The server creates the session and announces it in the chat, so there is
 * nothing to send here afterwards.
 */
export function GameSetup({
  open,
  onClose,
  conversationId,
  lockConversation,
  gameKey,
  onCreated,
}: GameSetupProps) {
  const myId = useMyId();
  const { games, loading, failed } = useGameCatalog();
  const conversations = useChat((state) => state.conversations);

  /*
   * Der Chat, aus dem man kommt, bleibt drin – auch wenn er archiviert ist.
   *
   * Archivierte gehören nicht in die Auswahlliste; wer aber IN einem
   * archivierten Chat auf „Spiel“ tippt, kam vorher gar nicht weiter: `chat`
   * war null, `ready` blieb false, „Spiel starten“ war dauerhaft grau, und
   * statt eines Hinweises erschien die Chat-Auswahl, deren Änderung der
   * Effekt unten sofort wieder zurückdrehte. Ein Spiel im archivierten Chat
   * ist nichts Verbotenes – der Server kennt die Unterscheidung nicht einmal.
   */
  const chats = useMemo(
    () =>
      conversations.filter(
        (item) => item.members.length > 1 && (!item.archived || item.id === conversationId),
      ),
    [conversations, conversationId],
  );

  const [chatId, setChatId] = useState<string>(conversationId ?? chats[0]?.id ?? '');
  const [selectedGame, setSelectedGame] = useState<string>(gameKey ?? '');
  const [opponents, setOpponents] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (conversationId) setChatId(conversationId);
    else if (!chatId && chats[0]) setChatId(chats[0].id);
  }, [conversationId, chats, chatId]);

  /*
   * `gameKey` ist eine VORauswahl, kein Zwang.
   *
   * Vorher stand hier schlicht `if (gameKey) setSelectedGame(gameKey)` mit
   * `selectedGame` im Abhängigkeitsfeld. Damit waren die Kacheln tot: Ein
   * Tipp auf „Vier gewinnt“ setzte die Auswahl, das liess den Effekt erneut
   * laufen, und der stellte sofort wieder auf `gameKey` zurück. Sichtbar war
   * nur, dass sich beim Antippen nichts rührt.
   *
   * Derselbe Merker-Kniff wie bei `pickedFor` weiter unten: Der Vorschlag
   * greift einmal je Wert und lässt danach los.
   */
  const vorgabeGesetzt = useRef<string | null>(null);
  useEffect(() => {
    if (gameKey) {
      if (vorgabeGesetzt.current !== gameKey) {
        vorgabeGesetzt.current = gameKey;
        setSelectedGame(gameKey);
      }
      return;
    }
    vorgabeGesetzt.current = null;
    if (!selectedGame && games[0]) setSelectedGame(games[0].key);
  }, [gameKey, games, selectedGame]);

  const chat = chats.find((item) => item.id === chatId) ?? null;
  const info = games.find((item) => item.key === selectedGame) ?? null;
  const candidates = useMemo(
    () => (chat ? chat.members.filter((member) => member.userId !== myId) : []),
    [chat, myId],
  );

  // Direct chats have exactly one opponent, groups start with nobody picked.
  // Only a different chat resets the selection – an incoming message must not.
  const pickedFor = useRef('');
  useEffect(() => {
    if (!chat || pickedFor.current === chat.id) return;
    pickedFor.current = chat.id;
    setOpponents(
      chat.type === 'direct'
        ? chat.members.filter((member) => member.userId !== myId).map((member) => member.userId)
        : [],
    );
  }, [chat, myId]);

  const maxOpponents = Math.max(1, (info?.maxPlayers ?? 2) - 1);
  /*
   * Hier startet man immer eine Partie MIT jemandem aus dem Chat.
   *
   * Pressen gegen den Spiegel (allein üben) braucht keinen Chat und taucht in
   * keinem auf: Der Zugang steht in der Spiele-Übersicht (`GamesScreen`), die
   * Übung hat keine Karte im Chat. Deshalb mindestens ein Gegner, auch wenn das
   * Spiel selbst mit einer Person läuft.
   */
  const minOpponents = Math.max(1, (info?.minPlayers ?? 2) - 1);
  const mitspielende = opponents;
  const ready = Boolean(chat && info) && mitspielende.length >= minOpponents;

  /*
   * Bei genau einem freien Platz ERSETZT der neue Tipp den alten.
   *
   * Beide Spiele sind für zwei Personen, `maxOpponents` ist also 1. Wer sich
   * vertippt hatte, bekam beim zweiten Namen nur „Dieses Spiel ist für zwei
   * Personen“ – und musste erst den ersten Namen wieder abwählen. Bei einer
   * Auswahl, die ohnehin nur einen zulässt, ist Umschalten das Erwartete;
   * abgelehnt wird nur da, wo wirklich mehrere Plätze zu vergeben sind.
   */
  function toggleOpponent(userId: string) {
    setOpponents((current) => {
      if (current.includes(userId)) return current.filter((item) => item !== userId);
      if (maxOpponents === 1) return [userId];
      if (current.length >= maxOpponents) {
        toast(`Höchstens ${maxOpponents} Mitspielende`, 'info');
        return current;
      }
      return [...current, userId];
    });
  }

  async function create() {
    if (!chat || !info || saving) return;
    if (mitspielende.length < minOpponents) {
      toast('Wähle mindestens eine Person aus', 'error');
      return;
    }
    setSaving(true);
    try {
      const session = await api.games.create({
        conversationId: chat.id,
        gameKey: info.key,
        opponentIds: mitspielende,
      });
      toast(`${info.name} gestartet`, 'success');
      onClose();
      onCreated?.(session);
    } catch (error) {
      toast(errorMessage(error, 'Spiel konnte nicht gestartet werden'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Spiel starten">
      {loading && games.length === 0 && <Spinner label="Spiele werden geladen …" />}

      {failed && games.length > 0 && (
        <p className="game-note">
          Der Katalog konnte nicht geladen werden – du siehst die Spiele, die deine App kennt.
        </p>
      )}

      {games.length > 0 && (
        <div className="game-pick" role="radiogroup" aria-label="Spiel">
          {games.map((game) => (
            <button
              key={game.key}
              type="button"
              role="radio"
              aria-checked={game.key === selectedGame}
              className={`game-pick-tile${game.key === selectedGame ? ' is-active' : ''}`}
              onClick={() => setSelectedGame(game.key)}
            >
              <span className="game-pick-emoji" aria-hidden="true">
                {game.emoji}
              </span>
              <span className="game-pick-name">{game.name}</span>
              <span className="game-pick-players">
                {game.minPlayers === game.maxPlayers
                  ? `${game.minPlayers} Spielende`
                  : `${game.minPlayers}–${game.maxPlayers} Spielende`}
              </span>
            </button>
          ))}
        </div>
      )}

      {info && <p className="game-note">{info.description}</p>}
      {info && info.key === 'pressen' && chat && mitspielende.length > 0 && (
        <p className="game-note" data-testid="pressen-aufstellung">
          {pressenAufstellung(mitspielende.length + 1)}
        </p>
      )}

      {chats.length === 0 ? (
        <EmptyState
          emoji="💬"
          title="Noch kein Chat"
          description="Spiele laufen immer in einem Chat. Starte zuerst einen Chat mit jemandem."
          action={
            <Link className="btn btn-primary" to="/chats" onClick={onClose}>
              Zu den Chats
            </Link>
          }
        />
      ) : (
        <>
          {lockConversation && chat ? (
            <p className="game-note">
              Die Partie läuft in <strong>{conversationLabel(chat, myId)}</strong>.
            </p>
          ) : (
            <div className="field">
              <label htmlFor="game-chat">Chat</label>
              <select
                id="game-chat"
                className="select"
                value={chatId}
                onChange={(event) => setChatId(event.target.value)}
              >
                {chats.map((item) => (
                  <option key={item.id} value={item.id}>
                    {conversationLabel(item, myId)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {chat && chat.type === 'direct' && candidates[0] && (
            <p className="game-note">
              Gegner: <strong>{memberLabel(chat, candidates[0].userId)}</strong>
            </p>
          )}

          {chat && chat.type === 'group' && (
            <div className="stack">
              <span className="game-label">
                Wer spielt mit?{' '}
                <span className="muted">
                  ({opponents.length}/{maxOpponents})
                </span>
              </span>
              {/*
                  Ein `radio` ohne `radiogroup` ist für eine Vorlesehilfe keine
                  Wahl, sondern ein loser Knopf: Sie sagt weder „1 von 3" noch,
                  dass die Auswahl einander ausschliesst. Bei `checkbox` ist die
                  Gruppe entbehrlich, bei `radio` gehört sie dazu.
              */}
              <div
                className="list game-member-list"
                role={maxOpponents === 1 ? 'radiogroup' : undefined}
                aria-label={maxOpponents === 1 ? 'Wer spielt mit?' : undefined}
              >
                {candidates.map((member) => {
                  const checked = opponents.includes(member.userId);
                  return (
                    <button
                      key={member.userId}
                      type="button"
                      // Ein Platz heisst eine Wahl – dafür steht `radio`, nicht
                      // `checkbox`. Vorlesende Geräte sagen damit dasselbe wie
                      // das Verhalten: Der nächste Name ersetzt den vorigen.
                      role={maxOpponents === 1 ? 'radio' : 'checkbox'}
                      aria-checked={checked}
                      className="list-row game-member"
                      onClick={() => toggleOpponent(member.userId)}
                    >
                      <Avatar
                        name={memberLabel(chat, member.userId)}
                        id={member.userId}
                        url={member.user.avatarUrl}
                        size={36}
                      />
                      <span className="truncate" style={{ flex: 1 }}>
                        {memberLabel(chat, member.userId)}
                      </span>
                      <span className={`game-check${checked ? ' is-on' : ''}`} aria-hidden="true">
                        {checked ? '✓' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={!ready || saving}
            onClick={() => void create()}
          >
            {saving ? 'Wird gestartet …' : 'Spiel starten'}
          </button>
        </>
      )}
    </Sheet>
  );
}
